import { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonContent,
  IonText,
  IonImg,
  IonBackButton,
  IonIcon,
  useIonViewDidEnter,
  useIonViewDidLeave
} from '@ionic/react';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { playOutline } from 'ionicons/icons';
import ReactPlayer from 'react-player/lazy';
import SanatizedHtml from 'components/SanatizedHtml';
import Loader from './Loader';
import {
  capitalize,
  getUrlFromPath,
  getPrivateFile
} from 'libs/helpers/helper.lib';

import educationalResourceStore from 'stores/educational-resource.store';

import { ReactComponent as DownloadFileIcon } from '../assets/file_download.svg';

import classes from './style.module.scss';

const ER_EDUCATIONAL_RESOURCES_FIELDS = `
    id
    name
    description
    thumbnail
    type
    article
    image
    video_url
    file
    er_categories {
      id
      name
    }
    er_tags {
      id
      name
    }
    createdBy {
      __typename
      ... on BarwisAdmin {
        id
        user {
          id
          first_name
          last_name
        }
      }
    }
    created_at
    updated_at
`;

const EducationalResourceSinglePage = () => {
  const { id } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const educationalResource = educationalResourceStore.getObject();
  const isLoading = educationalResourceStore.getIsLoading();

  const fetchEducationalResource = async () => {
    try {
      educationalResourceStore.getObjectById(id);
    } catch (error) {
      console.log(error);
    }
  };

  useIonViewDidEnter(() => {
    educationalResourceStore.setFields(ER_EDUCATIONAL_RESOURCES_FIELDS);
    fetchEducationalResource();
  });

  useIonViewDidLeave(() => {
    educationalResourceStore.setObject({});
  });

  const _renderMediaArticle = () => {
    if (!educationalResource.thumbnail) {
      return '';
    }
    return (
      <IonImg
        src={getUrlFromPath(educationalResource.thumbnail)}
        alt={educationalResource.name}
      />
    );
  };

  const _renderMediaImage = () => {
    return (
      <IonImg
        src={getUrlFromPath(educationalResource.image)}
        alt={educationalResource.name}
      />
    );
  };

  const _renderMediaVideo = () => {
    return (
      <ReactPlayer
        url={getUrlFromPath(educationalResource.video_url)}
        width='100%'
        height='30vh'
        controls={true}
        playIcon={<IonIcon color='dark' size='large' icon={playOutline} />}
        stopOnUnmount={true}
        playing={isPlaying}
        onPlay={() => {
          setIsPlaying(true);
          ScreenOrientation.unlock();
        }}
        onPause={() => {
          setIsPlaying(false);
          ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.PORTRAIT);
        }}
        onEnded={() => {
          ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.PORTRAIT);
        }}
        config={{
          youtube: {
            playerVars: {
              showinfo: 0,
              modestbranding: 1,
              rel: 0
            }
          }
        }}
        {...(educationalResource.thumbnail && {
          light: getUrlFromPath(educationalResource.thumbnail)
        })}
      />
    );
  };

  const _renderMediaFile = () => {
    if (!educationalResource.thumbnail) {
      return '';
    }
    return (
      <IonImg
        src={getUrlFromPath(educationalResource.thumbnail)}
        alt={educationalResource.name}
      />
    );
  };

  const _renderMediaContainer = () => {
    let content = '';
    const type = educationalResource.type;
    switch (type) {
      case 'article':
        content = _renderMediaArticle();
        break;
      case 'image':
        content = _renderMediaImage();
        break;
      case 'video':
        content = _renderMediaVideo();
        break;
      case 'file':
        content = _renderMediaFile();
        break;
      default:
        return;
    }
    return (
      <div
        className={`${classes.mediaContainer} ${
          educationalResource.type === 'video' && classes.mediaContainerVideo
        }`}
        onClick={() => setIsPlaying((prevValue) => !prevValue)}
      >
        {content}
      </div>
    );
  };

  const hasMedia = () => {
    const type = educationalResource.type;
    let isMediaAvailable = false;
    switch (type) {
      case 'article':
        if (educationalResource.thumbnail) isMediaAvailable = true;
        break;
      case 'image':
        if (educationalResource.image) isMediaAvailable = true;
        break;
      case 'file':
        if (educationalResource.thumbnail) isMediaAvailable = true;
        break;
      default:
    }

    return isMediaAvailable;
  };

  const _renderPageContent = () => {
    return (
      <>
        <IonHeader className={classes.ionHeader}>
          <IonToolbar className={`${hasMedia() && classes.absolute}`}>
            <IonButtons slot='start'>
              <IonBackButton></IonBackButton>
            </IonButtons>
            {educationalResource.type === 'file' && (
              <IonButtons slot='end'>
                <IonButton
                  fill='solid'
                  color='secondary'
                  className={classes.downloadIconBox}
                  size='large'
                  onClick={() =>
                    getPrivateFile(
                      educationalResource.file,
                      educationalResource.name
                    )
                  }
                  download
                >
                  <DownloadFileIcon />
                </IonButton>
              </IonButtons>
            )}
          </IonToolbar>
          <div className={classes.headContainer}>
            {_renderMediaContainer()}
            <div
              className={`${classes.infoContainer} ${
                hasMedia() && classes.hasMedia
              }`}
            >
              <IonText className={classes.erTitle}>
                {capitalize(educationalResource.name)}
              </IonText>
              <IonText className={classes.erSubTitle}>
                {educationalResource.er_categories
                  ?.map((er) => capitalize(er.name))
                  .join(', ')}
              </IonText>
              <IonText className={classes.author}>
                <span className={classes.createdBy}>Created By</span>
                <span className={classes.authorName}>
                  {educationalResource.createdBy?.user.first_name}{' '}
                  {educationalResource.createdBy?.user.last_name}
                </span>
              </IonText>
            </div>
          </div>
        </IonHeader>
        <IonContent className={classes.ionContent}>
          <SanatizedHtml dirtyHtml={educationalResource.description} />
        </IonContent>
      </>
    );
  };

  return (
    <IonPage id='educational-resource-single'>
      {!isLoading && educationalResource?.id ? (
        _renderPageContent()
      ) : (
        <Loader />
      )}
    </IonPage>
  );
};

export default observer(EducationalResourceSinglePage);
